import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import {
  Plus, Edit3, Trash2, Play, ArrowLeft, CheckCircle2, XCircle, Loader2, Sparkles,
  Clock, AlertTriangle, PauseCircle, Tags, Package, ChevronDown, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { apiClient } from '@/services/api';
import { toast } from 'sonner';
import { timeAgo } from '@/utils/cn';

interface Connection {
  id: number;
  agent_id: string;
  name: string;
  db_type: 'postgres' | 'mysql' | 'sqlserver' | 'firebird';
  server_hint?: string;
  database_hint?: string;
}

interface Routine {
  id: number;
  connection_id: number;
  name: string;
  target: 'products' | 'promotions' | 'stock' | 'categories';
  sql_query: string;
  interval_seconds: number;
  initial_strategy: string;
  max_rows: number;
  timeout_seconds: number;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  // Status de execução (vindo do cursor + execuções)
  is_running?: boolean;
  last_execution_at?: string | null;
  last_status?: 'ok' | 'error' | 'empty' | 'partial' | null;
  last_rows?: number | null;
  last_error_message?: string | null;
  next_run_at?: string | null;
}

// ----- Helpers de apresentação -------------------------------------------

const FRIENDLY: Record<string, { label: string; desc: string; icon: React.ReactNode }> = {
  '__discover-categorias': {
    label: 'Descoberta de categorias',
    desc: 'Lê os departamentos e categorias do ERP para você escolher o que exibir.',
    icon: <Tags className="w-4 h-4 text-violet-600" />,
  },
  'produtos-snapshot': {
    label: 'Catálogo completo (3/3h)',
    desc: 'Rede de segurança: relê todo o catálogo no escopo a cada 3 horas.',
    icon: <Package className="w-4 h-4 text-emerald-600" />,
  },
  'produtos-novos': {
    label: 'Alterações do dia',
    desc: 'Sincroniza preços e produtos alterados/novos de hoje (via data de alteração, indexada). Leve, roda a cada 5 min.',
    icon: <Package className="w-4 h-4 text-sky-600" />,
  },
};

function friendly(name: string) {
  return FRIENDLY[name] ?? { label: name, desc: '', icon: <Sparkles className="w-4 h-4 text-zinc-400" /> };
}

function formatInterval(s: number): string {
  if (s % 86400 === 0) return `${s / 86400} dia${s / 86400 > 1 ? 's' : ''}`;
  if (s % 3600 === 0) return `${s / 3600} h`;
  if (s % 60 === 0) return `${s / 60} min`;
  return `${s}s`;
}

function formatNextRun(nextRunAt?: string | null): string {
  if (!nextRunAt) return '—';
  const diffMs = new Date(nextRunAt).getTime() - Date.now();
  if (diffMs <= 0) return 'no próximo poll';
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'em menos de 1 min';
  if (mins < 60) return `em ${mins} min`;
  const hrs = Math.round(mins / 60);
  return `em ${hrs} h`;
}

type StatusKind = 'running' | 'error' | 'waiting' | 'ok' | 'disabled';

function statusOf(r: Routine): { kind: StatusKind; label: string; cls: string; icon: React.ReactNode } {
  if (!r.enabled)
    return { kind: 'disabled', label: 'Desativada', cls: 'bg-zinc-100 text-zinc-600 border-zinc-200', icon: <PauseCircle className="w-3.5 h-3.5" /> };
  if (r.is_running)
    return { kind: 'running', label: 'Rodando agora', cls: 'bg-sky-100 text-sky-700 border-sky-200', icon: <Loader2 className="w-3.5 h-3.5 animate-spin" /> };
  if (r.last_status === 'error')
    return { kind: 'error', label: 'Erro na última execução', cls: 'bg-red-100 text-red-700 border-red-200', icon: <XCircle className="w-3.5 h-3.5" /> };
  if (!r.last_execution_at)
    return { kind: 'waiting', label: 'Aguardando 1ª execução', cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: <Clock className="w-3.5 h-3.5" /> };
  return { kind: 'ok', label: 'OK', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: <CheckCircle2 className="w-3.5 h-3.5" /> };
}

export function RoutinesPage() {
  const [editing, setEditing] = useState<Routine | 'new' | null>(null);
  const [showSql, setShowSql] = useState<Set<number>>(new Set());
  const [pickedConnId, setPickedConnId] = useState<number | null>(null);
  const qc = useQueryClient();

  const { data: connections = [] } = useQuery({
    queryKey: ['agent-etl', 'connections'],
    queryFn: () => apiClient.agentEtl.listConnections().then(r => r.data.connections as Connection[]),
  });
  // Conexão usada ao gerar/regerar rotinas. Default = primeira.
  const activeConnId = pickedConnId ?? connections[0]?.id ?? null;
  const { data: routines = [], isLoading } = useQuery({
    queryKey: ['agent-etl', 'routines'],
    queryFn: () => apiClient.agentEtl.listRoutines().then(r => r.data.routines as Routine[]),
    refetchInterval: 15_000, // atualiza status (rodando/agendado) sozinho
  });

  const del = useMutation({
    mutationFn: (id: number) => apiClient.agentEtl.deleteRoutine(id),
    onSuccess: () => { toast.success('Rotina removida.'); qc.invalidateQueries({ queryKey: ['agent-etl', 'routines'] }); },
    onError: () => toast.error('Falha ao remover.'),
  });

  const trigger = useMutation({
    mutationFn: (id: number) => apiClient.agentEtl.triggerRoutine(id),
    onSuccess: () => { toast.success('Enfileirada — roda no próximo poll do agente.'); qc.invalidateQueries({ queryKey: ['agent-etl', 'routines'] }); },
    onError: () => toast.error('Falha ao acionar rotina.'),
  });

  const connDel = useMutation({
    mutationFn: (id: number) => apiClient.agentEtl.deleteConnection(id),
    onSuccess: () => {
      toast.success('Conexão removida do portal.');
      qc.invalidateQueries({ queryKey: ['agent-etl', 'connections'] });
      qc.invalidateQueries({ queryKey: ['agent-etl', 'routines'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Falha ao remover conexão.'),
  });

  const seed = useMutation({
    mutationFn: () => {
      const connId = activeConnId;
      if (!connId) throw new Error('Selecione uma conexão');
      return apiClient.agentEtl.seedVisualErpRoutines(connId, false);
    },
    onSuccess: (resp) => {
      const r = resp.data as { created: number; updated: number };
      toast.success(`Rotinas padrão prontas: ${r.created} criada(s), ${r.updated} atualizada(s).`);
      qc.invalidateQueries({ queryKey: ['agent-etl', 'routines'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Falha ao gerar rotinas padrão.'),
  });

  function toggleSql(id: number) {
    const next = new Set(showSql);
    next.has(id) ? next.delete(id) : next.add(id);
    setShowSql(next);
  }

  if (editing) {
    return (
      <RoutineEditor
        routine={editing === 'new' ? null : editing}
        connections={connections}
        onClose={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['agent-etl', 'routines'] }); }}
      />
    );
  }

  // Ordena: padrão primeiro (discovery, snapshot, novos), depois custom.
  const STD_ORDER = ['__discover-categorias', 'produtos-snapshot', 'produtos-novos'];
  const sorted = [...routines].sort((a, b) => {
    const ia = STD_ORDER.indexOf(a.name), ib = STD_ORDER.indexOf(b.name);
    if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    return a.name.localeCompare(b.name);
  });

  const hasErrors = routines.some(r => r.enabled && r.last_status === 'error');

  return (
    <div className="container-app py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/settings/agent" className="text-xs text-muted-foreground hover:underline flex items-center gap-1 mb-2">
            <ArrowLeft className="w-3 h-3" /> Voltar para agentes
          </Link>
          <h1 className="text-2xl font-semibold">Rotinas de sincronização</h1>
          <p className="text-sm text-muted-foreground mt-1">
            O agente executa estas consultas no ERP do cliente periodicamente e envia os dados para o MyShow.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/settings/agent/catalog">
            <Button variant="outline" disabled={!connections.length}>
              <Tags className="w-4 h-4 mr-1" /> Escopar por categorias
            </Button>
          </Link>
          <Button onClick={() => setEditing('new')} disabled={!connections.length}>
            <Plus className="w-4 h-4 mr-1" /> Nova rotina
          </Button>
        </div>
      </div>

      {!connections.length && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          Nenhuma conexão de banco cadastrada ainda. Cadastre a conexão no app do agente
          (botão <strong>Cadastrar ERP</strong>) — ela aparece aqui automaticamente.
        </div>
      )}

      {connections.length > 0 && (
        <div className="rounded-lg border border-zinc-200 bg-white p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
              Conexões ({connections.length})
            </span>
            {connections.length > 1 && (
              <span className="text-xs text-amber-700 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Mais de uma conexão — remova as que não usa
              </span>
            )}
          </div>
          <div className="space-y-1.5">
            {connections.map(c => {
              const isActive = c.id === activeConnId;
              return (
                <div key={c.id} className={`flex items-center gap-3 rounded-md border px-3 py-2 text-sm ${isActive ? 'border-violet-300 bg-violet-50' : 'border-zinc-200'}`}>
                  <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                    <input
                      type="radio"
                      name="active-conn"
                      checked={isActive}
                      onChange={() => setPickedConnId(c.id)}
                      className="w-4 h-4"
                    />
                    <span className="font-mono text-xs text-zinc-500">#{c.id}</span>
                    <span className="font-medium truncate">{c.name}</span>
                    <Badge variant="outline" className="text-xs">{c.db_type}</Badge>
                    {isActive && <Badge className="text-xs">usada nas rotinas</Badge>}
                  </label>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => {
                      if (confirm(`Excluir a conexão #${c.id} "${c.name}" do portal?\n\nRotinas que a usam vão parar. Use isso para limpar conexões órfãs.`))
                        connDel.mutate(c.id);
                    }}
                    disabled={connDel.isPending}
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Excluir
                  </Button>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            A conexão marcada é usada ao <strong>Gerar/Regerar rotinas padrão</strong>. Ela precisa
            ter o mesmo ID cadastrado no app do agente.
          </p>
        </div>
      )}

      {hasErrors && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            Há rotina(s) com erro. Causa comum: a conexão usada pela rotina não está cadastrada no agente
            com o mesmo ID. Veja a mensagem em cada card abaixo ou os{' '}
            <Link to="/settings/agent/logs" className="underline">logs de execução</Link>.
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Carregando…</div>
      ) : !routines.length ? (
        <div className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-8 text-center">
          <Sparkles className="w-7 h-7 mx-auto mb-2 text-violet-600" />
          <p className="text-sm font-medium">Nenhuma rotina ainda.</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
            Gere as 3 rotinas padrão do Visual ERP de uma vez: descoberta de categorias,
            catálogo completo (30 min) e produtos novos (5 min).
          </p>
          <Button className="mt-4" onClick={() => seed.mutate()} disabled={!connections.length || seed.isPending}>
            {seed.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
            Gerar rotinas padrão
          </Button>
          {!connections.length && (
            <p className="text-xs text-amber-700 mt-2">Cadastre uma conexão no app do agente primeiro.</p>
          )}
        </div>
      ) : (
        <>
          {/* Resumo agregado de status */}
          <StatusSummary routines={routines} onRegenerate={() => seed.mutate()} regenerating={seed.isPending} canSeed={!!connections.length} />


          <div className="grid gap-3">
            {sorted.map(r => {
              const conn = connections.find(c => c.id === r.connection_id);
              const st = statusOf(r);
              const f = friendly(r.name);
              const isStd = STD_ORDER.includes(r.name);
              const sqlOpen = showSql.has(r.id);
              return (
                <div key={r.id} className="rounded-lg border border-zinc-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Título + status */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {f.icon}
                        <span className="font-medium">{f.label}</span>
                        <span className={`inline-flex items-center gap-1 text-xs font-medium rounded-full border px-2 py-0.5 ${st.cls}`}>
                          {st.icon} {st.label}
                        </span>
                        {!isStd && <Badge variant="outline" className="text-xs">{r.target}</Badge>}
                        {conn && <span className="text-xs text-muted-foreground">{conn.db_type} · {conn.name}</span>}
                      </div>

                      {f.desc && <p className="text-xs text-muted-foreground mt-1">{f.desc}</p>}

                      {/* Linha de métricas: a cada / última / próxima / linhas */}
                      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs">
                        <span className="text-muted-foreground">
                          Frequência: <span className="text-foreground font-medium">a cada {formatInterval(r.interval_seconds)}</span>
                        </span>
                        <span className="text-muted-foreground">
                          Última: <span className="text-foreground font-medium">
                            {r.last_execution_at ? timeAgo(r.last_execution_at) : 'nunca'}
                          </span>
                          {r.last_rows != null && r.last_status !== 'error' && (
                            <span className="text-foreground"> · {r.last_rows.toLocaleString('pt-BR')} linhas</span>
                          )}
                        </span>
                        {r.enabled && !r.is_running && (
                          <span className="text-muted-foreground">
                            Próxima: <span className="text-foreground font-medium">{formatNextRun(r.next_run_at)}</span>
                          </span>
                        )}
                      </div>

                      {/* Erro inline */}
                      {st.kind === 'error' && r.last_error_message && (
                        <pre className="mt-2 text-xs font-mono bg-red-50 border border-red-200 rounded p-2 overflow-x-auto whitespace-pre-wrap text-red-800">
                          {r.last_error_message}
                        </pre>
                      )}

                      {/* SQL colapsável */}
                      <button
                        onClick={() => toggleSql(r.id)}
                        className="mt-2 text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                      >
                        {sqlOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        {sqlOpen ? 'Ocultar SQL' : 'Ver SQL'}
                      </button>
                      {sqlOpen && (
                        <pre className="mt-1 text-xs bg-zinc-50 border border-zinc-200 rounded p-2 overflow-x-auto font-mono">
                          {r.sql_query}
                        </pre>
                      )}
                    </div>

                    <div className="flex flex-col gap-1 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => trigger.mutate(r.id)} disabled={!r.enabled || r.is_running || trigger.isPending}>
                        <Play className="w-3 h-3 mr-1" /> Rodar agora
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setEditing(r)}>
                        <Edit3 className="w-3 h-3 mr-1" /> Editar
                      </Button>
                      <Button
                        variant="outline" size="sm"
                        onClick={() => { if (confirm(`Remover rotina "${f.label}"?`)) del.mutate(r.id); }}
                      >
                        <Trash2 className="w-3 h-3 mr-1" /> Excluir
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ====================================================================
// Resumo agregado de status
// ====================================================================

function StatusSummary({
  routines, onRegenerate, regenerating, canSeed,
}: { routines: Routine[]; onRegenerate: () => void; regenerating: boolean; canSeed: boolean }) {
  const counts = { ok: 0, running: 0, error: 0, waiting: 0, disabled: 0 } as Record<StatusKind, number>;
  for (const r of routines) counts[statusOf(r).kind]++;

  const lastSync = routines
    .map(r => r.last_execution_at)
    .filter((x): x is string => !!x)
    .sort()
    .pop();

  const chips: { kind: StatusKind; label: string; cls: string; icon: React.ReactNode }[] = [
    { kind: 'running',  label: 'Rodando',      cls: 'bg-sky-50 text-sky-700 border-sky-200',           icon: <Loader2 className="w-3.5 h-3.5 animate-spin" /> },
    { kind: 'ok',       label: 'OK',           cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
    { kind: 'error',    label: 'Com erro',     cls: 'bg-red-50 text-red-700 border-red-200',            icon: <XCircle className="w-3.5 h-3.5" /> },
    { kind: 'waiting',  label: 'Aguardando',   cls: 'bg-amber-50 text-amber-800 border-amber-200',      icon: <Clock className="w-3.5 h-3.5" /> },
    { kind: 'disabled', label: 'Desativadas',  cls: 'bg-zinc-50 text-zinc-600 border-zinc-200',         icon: <PauseCircle className="w-3.5 h-3.5" /> },
  ];

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-3 flex flex-wrap items-center gap-3">
      <div className="flex flex-wrap gap-2">
        {chips.filter(c => counts[c.kind] > 0).map(c => (
          <span key={c.kind} className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full border px-2.5 py-1 ${c.cls}`}>
            {c.icon} {counts[c.kind]} {c.label}
          </span>
        ))}
        {routines.length === 0 && <span className="text-xs text-muted-foreground">Sem rotinas.</span>}
      </div>
      <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
        <span>Última sincronização: <span className="text-foreground font-medium">{lastSync ? timeAgo(lastSync) : 'nunca'}</span></span>
        <Button variant="outline" size="sm" onClick={onRegenerate} disabled={!canSeed || regenerating}>
          {regenerating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
          Regerar padrão
        </Button>
      </div>
    </div>
  );
}

// ====================================================================
// Editor de rotina (criar/editar)
// ====================================================================

function RoutineEditor({
  routine, connections, onClose,
}: { routine: Routine | null; connections: Connection[]; onClose: () => void }) {
  const isNew = routine == null;
  const [name, setName] = useState(routine?.name ?? '');
  const [connId, setConnId] = useState<number>(routine?.connection_id ?? connections[0]?.id ?? 0);
  const [sql, setSql] = useState(routine?.sql_query ?? 'SELECT codigo, descricao, preco_cents\nFROM produtos\nWHERE 1=1');
  const [target, setTarget] = useState<'products' | 'promotions' | 'stock' | 'categories'>(routine?.target ?? 'products');
  const [interval, setInterval] = useState(routine?.interval_seconds ?? 300);
  const [maxRows, setMaxRows] = useState(routine?.max_rows ?? 10000);
  const [timeout, setTimeout] = useState(routine?.timeout_seconds ?? 30);
  const [enabled, setEnabled] = useState(routine?.enabled ?? true);

  const [validation, setValidation] = useState<{ is_valid: boolean; error?: string } | null>(null);
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);

  async function validate() {
    setValidating(true);
    try {
      const { data } = await apiClient.agentEtl.validateSql(sql);
      setValidation(data);
    } catch {
      setValidation({ is_valid: false, error: 'Falha de rede' });
    } finally {
      setValidating(false);
    }
  }

  async function save() {
    if (!name.trim()) { toast.error('Nome obrigatório.'); return; }
    if (!connId)     { toast.error('Selecione uma conexão.'); return; }
    setSaving(true);
    const payload = {
      connection_id: connId,
      name: name.trim(),
      sql_query: sql,
      target,
      interval_seconds: interval,
      max_rows: maxRows,
      timeout_seconds: timeout,
      enabled,
    };
    try {
      if (isNew) await apiClient.agentEtl.createRoutine(payload);
      else       await apiClient.agentEtl.updateRoutine(routine!.id, payload);
      toast.success(isNew ? 'Rotina criada.' : 'Rotina atualizada.');
      onClose();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'Falha ao salvar.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="container-app py-8 space-y-6">
      <button onClick={onClose} className="text-xs text-muted-foreground hover:underline flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Voltar para rotinas
      </button>
      <h1 className="text-2xl font-semibold">{isNew ? 'Nova rotina' : 'Editar rotina'}</h1>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Nome</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ex: Produtos modificados nas últimas 24h" />
        </div>
        <div>
          <label className="text-sm font-medium">Conexão</label>
          <select
            className="mt-1 w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
            value={connId}
            onChange={(e) => setConnId(Number(e.target.value))}
          >
            <option value="">— selecione —</option>
            {connections.map(c => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.db_type})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium">Destino</label>
          <select
            className="mt-1 w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
            value={target}
            onChange={(e) => setTarget(e.target.value as any)}
          >
            <option value="products">products (products_cache)</option>
            <option value="categories">categories (product_categories)</option>
            <option value="promotions">promotions (promotions_cache)</option>
            <option value="stock">stock (placeholder)</option>
          </select>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <NumberField label="Intervalo (s)" value={interval} onChange={setInterval} min={30} max={86400} />
          <NumberField label="Timeout (s)"  value={timeout} onChange={setTimeout} min={1} max={600} />
          <NumberField label="Max rows"    value={maxRows} onChange={setMaxRows} min={1} max={1000000} step={100} />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-sm font-medium">SQL (somente SELECT/WITH)</label>
          <Button size="sm" variant="outline" onClick={validate} disabled={validating}>
            {validating ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Validar SQL'}
          </Button>
        </div>
        <div className="rounded-md border border-zinc-300 overflow-hidden">
          <Editor
            height="240px"
            defaultLanguage="sql"
            value={sql}
            onChange={(v) => { setSql(v ?? ''); setValidation(null); }}
            options={{ minimap: { enabled: false }, fontSize: 13, scrollBeyondLastLine: false, wordWrap: 'on' }}
          />
        </div>
        {validation && (
          <div className={`mt-2 text-sm flex items-start gap-2 rounded-md p-2 border ${
            validation.is_valid ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            {validation.is_valid ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <XCircle className="w-4 h-4 shrink-0" />}
            <span>{validation.is_valid ? 'SQL válido.' : validation.error}</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <label className="text-sm flex items-center gap-2">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Rotina ativa
        </label>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving}>
            {saving ? 'Salvando…' : (isNew ? 'Criar rotina' : 'Salvar alterações')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function NumberField({
  label, value, onChange, min, max, step = 1,
}: { label: string; value: number; onChange: (n: number) => void; min: number; max: number; step?: number }) {
  return (
    <div>
      <label className="text-xs font-medium">{label}</label>
      <Input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Math.max(min, Math.min(max, Number(e.target.value) || min)))}
      />
    </div>
  );
}
